import { ApolloClient, NormalizedCacheObject } from '@apollo/client'
import { GET_ORDER_STATUS } from 'account/components/AccountOrders'
import { PartialOrder } from 'account/models/PartialOrder'
import ErrorContextProvider from 'errors/components/ErrorContextProvider'
import { serverSideConfigs } from 'localization'
import ManageOrderCreateAccount from 'manageOrder/ManageOrderCreateAccount'
import { useRouter } from 'next/router'
import { useEffect } from 'react'
import { st } from 'server/utils/localization'
import createApolloClient from 'shared/apollo/createApolloClient'
import { FCGetServerSidePropsContext, getServerPageProps } from 'shared/utils/getServerSideProps'
import { parseCookies } from 'shared/utils/server-utils'

const TP = 'pages.manageOrder.orderNumber'

interface IManageOrderProps {
  order?: PartialOrder
  error?: string
  shouldShowLogin?: boolean
  refreshed?: boolean
  requiresLogin?: boolean
}

const ManageOrder = ({
  error,
  order,
  shouldShowLogin = false,
  requiresLogin = false,
  refreshed = false,
}: IManageOrderProps) => {
  const router = useRouter()

  useEffect(() => {
    if (requiresLogin && !refreshed) {
      // This is a hacky solution for the NavigationAccountMenu not updating
      // to show correct state if a user is programmatically logged out
      // then redirected to this page.
      router.replace(`${router.asPath}&refreshed=true`, `${router.asPath}&refreshed=true`, {
        locale: router.locale,
      })
    }
  }, [refreshed, requiresLogin])

  return (
    <ErrorContextProvider>
      <ManageOrderCreateAccount error={error} order={order} shouldShowLogin={shouldShowLogin} />
    </ErrorContextProvider>
  )
}

export const getServerSideProps = async (context: FCGetServerSidePropsContext) => {
  const { res, req, locale, query } = context
  const { jwt, currency } = parseCookies(req)
  const orderNumber = query.orderNumber
  const md5EmailHash: string = req.query?.q
  const shouldShowLogin: boolean = req.query?.shouldShowLogin === 'true'
  const requiresLogin: boolean = req.query?.requiresLogin === 'true'
  const refreshed: boolean = req.query?.refreshed === 'true'
  const apollo: ApolloClient<NormalizedCacheObject> = createApolloClient(
    {},
    {
      getCsrf: () => null,
      getToken: () => jwt,
      req,
      res,
      currency,
    },
  )

  if (!orderNumber || !md5EmailHash) {
    return {
      props: {
        ...(await getServerPageProps(context)),
        ...(await serverSideConfigs(res, locale)),
      },
    }
  }

  try {
    const { data } = await apollo.query({
      query: GET_ORDER_STATUS,
      variables: {
        input: {
          orderNumber: Number(orderNumber),
          md5EmailHash,
        },
      },
    })
    return {
      props: {
        ...(await getServerPageProps(context)),
        ...(await serverSideConfigs(res, locale)),
        order: data.getOrderStatus,
        shouldShowLogin: shouldShowLogin || requiresLogin,
        error:
          requiresLogin &&
          st(`${TP}.requiresLoginMessage`, {
            defaultValue: 'Please log in with the email address used to place this order.',
            req,
            locale,
          }),
        requiresLogin,
        refreshed,
      },
    }
  } catch (error) {
    return {
      props: {
        ...(await getServerPageProps(context)),
        ...(await serverSideConfigs(res, locale)),
        error: st(`${TP}.orderNotFound`, {
          defaultValue: 'Order not found. Please try again.',
          req,
          locale,
        }),
        requiresLogin,
      },
    }
  }
}

export default ManageOrder
